import request from "../../config/axios";
import { 
  validateCheckoutData, 
  prepareOrderData, 
  getPaymentEndpoint,
  handleCheckoutResponse,
  getCheckoutErrorMessage,
} from "./CheckoutValidation";

/**
 * Enhanced checkout service with better error handling and validation
 */
class CheckoutService {
  getCODIdempotencyKey() {
    const storageKey = "lianne_cod_idempotency_key";
    const existingKey = sessionStorage.getItem(storageKey);
    if (existingKey) {
      return existingKey;
    }

    const generatedKey =
      window.crypto?.randomUUID?.() ||
      `cod-${Date.now()}-${Math.random().toString(36).slice(2, 18)}`;
    sessionStorage.setItem(storageKey, generatedKey);
    return generatedKey;
  }
  
  /**
   * Submit checkout form with enhanced validation and error handling
   */
  async submitCheckout(formData, cartItems, orderTotals, locationData) {
    try {
      // Step 1: Validate data
      const validation = validateCheckoutData(formData, cartItems);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }

      // Step 2: Prepare order data
      const checkoutData = prepareOrderData(formData, cartItems, orderTotals, locationData);

      // Step 3: Get appropriate endpoint
      const endpoint = getPaymentEndpoint(formData.paymentMethod);
      const headers = {
        'Content-Type': 'application/json'
      };
      if (formData.paymentMethod === "cod") {
        headers["X-Idempotency-Key"] = this.getCODIdempotencyKey();
      }

      // Step 4: Submit to API
      const response = await request.post(
        endpoint,
        checkoutData,
        {
          headers,
          timeout: 30000, // 30 second timeout
        }
      );

      // Step 5: Handle response
      return handleCheckoutResponse(response, formData.paymentMethod);
      
    } catch (error) {
      if (error.response) {
        // Handle API error responses
        if (error.response.data) {
          return handleCheckoutResponse(error.response.data);
        }
      }

      // Handle network or other errors
      throw new Error(
        error.message || "Network error. Please check your connection and try again."
      );
    }
  }

  /**
   * Process VNPay callback
   */
  async handleVNPayCallback(urlParams) {
    try {
      const response = await request.post(
        '/wp-json/lianne-vnpay/v1/payment-callback', 
        urlParams,
        { timeout: 15000 }
      );
      
      if (response.success && response.payment_status === 'completed') {
        return {
          success: true,
          orderId: response.order_id,
          message: "Payment completed successfully!",
          data: response
        };
      } else {
        return {
          success: false,
          message: response.message || "Payment verification failed",
          data: response
        };
      }
    } catch (error) {
      return {
        success: false,
        message: "Failed to verify payment with server",
        error: error.message
      };
    }
  }

  /**
   * Clear cart after successful payment
   */
  async clearCart() {
    try {
      // Try REST API first
      await request.delete("/wp-json/wc/v3/cart/items");
      return true;
    } catch (error) {
      // Fallback to WC Ajax
      try {
        await request.post("/cart/?wc-ajax=remove_from_cart", {
          action: "clear_cart",
        });
        return true;
      } catch (fallbackError) {
        return false;
      }
    }
  }

  /**
   * Retry failed checkout with enhanced error recovery
   */
  async retryCheckout(originalData, retryCount = 0) {
    const maxRetries = 2;
    
    if (retryCount >= maxRetries) {
      throw new Error("Maximum retry attempts reached. Please try again later.");
    }

    try {
      // Add retry delay
      if (retryCount > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }

      return await this.submitCheckout(
        originalData.formData,
        originalData.cartItems,
        originalData.orderTotals,
        originalData.locationData
      );
    } catch (error) {
      if (retryCount < maxRetries - 1) {
        return this.retryCheckout(originalData, retryCount + 1);
      }
      
      throw error;
    }
  }
}

// Create singleton instance
const checkoutService = new CheckoutService();

// Export individual methods for backward compatibility
export const submitFormCheckout = (formData, cartItems, orderTotals, locationData) => {
  return checkoutService.submitCheckout(formData, cartItems, orderTotals, locationData);
};

export const handleVNPayCallback = (urlParams) => {
  return checkoutService.handleVNPayCallback(urlParams);
};

export const clearCart = () => {
  return checkoutService.clearCart();
};

export const getVNPayPaymentStatus = (orderId, lookupToken) => {
  return request.get("/wp-json/lianne-vnpay/v1/status", {
    params: {
      order_id: orderId,
      lookup_token: lookupToken,
    },
  }).then((response) => {
    if (response?.success === false) {
      throw new Error(getCheckoutErrorMessage(response.code));
    }

    return response;
  });
};

// Export service instance
export default checkoutService;
